import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './FeedbackWidget.css';

const API_BASE = import.meta.env?.VITE_API_URL || '';

const CATEGORIES = [
  { value: 'bug',        label: 'Bug / wrong behavior' },
  { value: 'data_issue', label: 'Data looks wrong' },
  { value: 'ui',         label: 'UI / display issue' },
  { value: 'idea',       label: 'Idea / suggestion' },
  { value: 'other',      label: 'Other' },
];

// Max screenshot payload we send (matches backend cap of 3 MB).
const MAX_SCREENSHOT_CHARS = 3 * 1024 * 1024;

// Re-encode an image (from capture or upload) down to a JPEG data URL
// that fits the size cap. Tries decreasing quality, then scales down.
async function toJpegDataUrl(source, maxChars = MAX_SCREENSHOT_CHARS) {
  const canvas = document.createElement('canvas');
  let w = source.videoWidth || source.naturalWidth || source.width;
  let h = source.videoHeight || source.naturalHeight || source.height;
  // Cap longest edge at 1920 to keep payloads sane.
  const maxEdge = 1920;
  if (Math.max(w, h) > maxEdge) {
    const s = maxEdge / Math.max(w, h);
    w = Math.round(w * s); h = Math.round(h * s);
  }
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(source, 0, 0, w, h);
  for (const q of [0.8, 0.6, 0.4]) {
    const url = canvas.toDataURL('image/jpeg', q);
    if (url.length <= maxChars) return url;
  }
  // Last resort: halve dimensions once.
  const half = document.createElement('canvas');
  half.width = Math.round(w / 2); half.height = Math.round(h / 2);
  half.getContext('2d').drawImage(canvas, 0, 0, half.width, half.height);
  return half.toDataURL('image/jpeg', 0.5);
}

// Capture the current tab via the browser's screen-share picker.
// Works with WebGL/Mapbox content (html2canvas does not).
async function captureScreen() {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    preferCurrentTab: true,   // Chrome: pre-selects this tab in the picker
    audio: false,
  });
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    await video.play();
    // One frame is enough.
    await new Promise(r => setTimeout(r, 150));
    return await toJpegDataUrl(video);
  } finally {
    stream.getTracks().forEach(t => t.stop());
  }
}

// Other components (e.g. the free-tier banner in SiteScreener) can open
// the feedback modal without prop-drilling by dispatching this event.
export function openFeedback() {
  window.dispatchEvent(new CustomEvent('seidr:open-feedback'));
}

export default function FeedbackWidget({ showFab = true }) {
  // Safe without an Auth0Provider: the SDK ships a default context with
  // isAuthenticated=false, so the hook itself never throws.
  const auth0 = useAuth0();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const reset = useCallback(() => {
    setMessage(''); setScreenshot(null); setError(null);
    setSent(false); setCategory('bug');
  }, []);

  // Open on demand from anywhere (banner button, etc.).
  useEffect(() => {
    const onOpen = () => { reset(); setOpen(true); };
    window.addEventListener('seidr:open-feedback', onOpen);
    return () => window.removeEventListener('seidr:open-feedback', onOpen);
  }, [reset]);

  const handleCapture = async () => {
    setError(null);
    // Hide the modal while the picker/capture runs so it isn't in the shot.
    setOpen(false);
    await new Promise(r => setTimeout(r, 300));
    try {
      const shot = await captureScreen();
      setScreenshot(shot);
    } catch (e) {
      if (e?.name !== 'NotAllowedError') {
        setError('Screen capture failed — you can attach an image file instead.');
      }
    } finally {
      setOpen(true);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setError(null);
    try {
      const img = new Image();
      const url = URL.createObjectURL(file);
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
      setScreenshot(await toJpegDataUrl(img));
      URL.revokeObjectURL(url);
    } catch {
      setError('Could not read that image file.');
    }
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (message.trim().length < 3) {
      setError('Please describe what you are seeing (a few words is fine).');
      return;
    }
    setBusy(true); setError(null);
    try {
      let token = null;
      if (auth0?.isAuthenticated) {
        try { token = await auth0.getAccessTokenSilently(); } catch { /* fall through */ }
      }
      const resp = await fetch(`${API_BASE}/api/v1/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          category,
          message: message.trim(),
          page_url: window.location.href,
          user_agent: navigator.userAgent,
          screenshot_b64: screenshot,
          context: {},
        }),
      });
      if (!resp.ok) {
        const detail = (await resp.json().catch(() => null))?.detail;
        throw new Error(detail || `HTTP ${resp.status}`);
      }
      setSent(true);
      setTimeout(() => { setOpen(false); reset(); }, 1800);
    } catch (e) {
      setError(`Could not send feedback: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {showFab && (
        <button
          type="button"
          className="fbw-fab"
          title="Send feedback"
          onClick={() => { reset(); setOpen(true); }}
        >
          💬 Feedback
        </button>
      )}

      {open && (
        <div className="fbw-overlay" onClick={() => !busy && setOpen(false)}>
          <div className="fbw-modal" onClick={e => e.stopPropagation()}>
            {sent ? (
              <div className="fbw-sent">
                <div className="fbw-sent-icon">✓</div>
                <div>Thanks — feedback received!</div>
              </div>
            ) : (
              <>
                <div className="fbw-header">
                  <span>Send feedback</span>
                  <button className="fbw-close" onClick={() => setOpen(false)} disabled={busy}>×</button>
                </div>

                <label className="fbw-label">Type</label>
                <select
                  className="fbw-select"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  disabled={busy}
                >
                  {CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>

                <label className="fbw-label">What are you seeing?</label>
                <textarea
                  className="fbw-textarea"
                  rows={4}
                  maxLength={5000}
                  placeholder="e.g. The map layer for protected areas isn't loading, or a parcel's distance to road seems off…"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  disabled={busy}
                  autoFocus
                />

                <label className="fbw-label">Screenshot (optional)</label>
                {screenshot ? (
                  <div className="fbw-shot">
                    <img src={screenshot} alt="screenshot preview" />
                    <button className="fbw-shot-remove" onClick={() => setScreenshot(null)} disabled={busy}>
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="fbw-shot-actions">
                    <button className="fbw-btn-secondary" onClick={handleCapture} disabled={busy}>
                      📷 Capture screen
                    </button>
                    <button className="fbw-btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                      📎 Attach image
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleFile}
                    />
                  </div>
                )}

                {error && <div className="fbw-error">{error}</div>}

                <button
                  className="fbw-btn-primary"
                  onClick={handleSubmit}
                  disabled={busy || message.trim().length < 3}
                >
                  {busy ? 'Sending…' : 'Send feedback'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
